import { recallStore } from '../recall-campaigns/store';
import { recallCallQueue } from '../triage/recallCalls';
import { getAgentHold, holdAgent, resumeAgent } from '../team-hold/teamHold';
import type { RcAgentData, RcChannel, RcWeek } from './types';

/**
 * Every headline on the recall agent page, **computed** (amendment 24 §0).
 *
 * The page failed staging because each figure was written down separately, so
 * they drifted: *per 100 contacted → 24* over 131 from 1,140, channel rows
 * summing to 268 against a headline of 131. None of those numbers can be written
 * down any more — they are all functions of the inputs in `mockData.ts`, and the
 * §8 acceptance list is a set of assertions about these functions.
 */

/** The headline. **The segments' sum**, so the breakdown cannot disagree with it. */
export function heroBooked(data: RcAgentData): number {
  return data.hero.segments.reduce((n, s) => n + s.value, 0);
}

/** `booked ÷ contacted × 100`, rounded. 131 from 1,140 is 11, and it said 24. */
export function perHundredContacted(data: RcAgentData): number {
  const contacted = data.hero.contacted;
  if (contacted === 0) return 0;
  return Math.round((heroBooked(data) / contacted) * 100);
}

/** `$24,890` — whole dollars, because the practice reads it as money on a schedule. */
export function productionScheduled(data: RcAgentData): string {
  return `$${data.hero.productionScheduled.toLocaleString('en-US')}`;
}

/** One channel's conversion. The row prints this rather than storing a percent. */
export function channelRate(ch: RcChannel): number {
  return ch.sent === 0 ? 0 : Math.round((ch.booked / ch.sent) * 100);
}

/** Bookings across the channels — asserted equal to `heroBooked` (§8). */
export function channelsBooked(data: RcAgentData): number {
  return data.channels.reduce((n, c) => n + c.booked, 0);
}

/** Messages across the channels — asserted equal to the *Sent* card (§8). */
export function channelsSent(data: RcAgentData): number {
  return data.channels.reduce((n, c) => n + c.sent, 0);
}

/**
 * The chart's top gridline. 0 / 20 / 40 while the weeks stay in the twenties and
 * thirties, and it grows in steps of 20 rather than clipping a tall week.
 */
export function weeklyAxisMax(weeks: RcWeek[]): number {
  const top = weeks.reduce((n, w) => Math.max(n, w.booked), 0);
  return Math.max(40, Math.ceil(top / 20) * 20);
}

/** The last four weeks — what the hero's 30 days covers. */
export function lastFourWeeks(weeks: RcWeek[]): number {
  return weeks.slice(-4).reduce((n, w) => n + w.booked, 0);
}

/**
 * A change figure is only shown with 60 days of history behind it (§3) — never a
 * percentage against nothing.
 */
export function showDeltas(data: RcAgentData): boolean {
  return data.historyDays >= 60;
}

/* ---- hold ---------------------------------------------------------------- *
 * One flag, read by four surfaces: this page's eyebrow and button, the AI Team
 * roster, and `/recall`'s banner (spec 22 §2). A boolean per page would be four
 * places for one fact — which is the failure this whole amendment is about.
 * -------------------------------------------------------------------------- */

/** Delegates to the unified AI-Team hold store (specs 36 + 37), so the roster
 * switch, this page's Hold button and a reload all agree. */
export const recallHoldStore = { get onHold(): boolean { return getAgentHold('recall').held; } };

export function setRecallHold(onHold: boolean): void {
  if (onHold) holdAgent('recall', { by: 'Ava Thompson', since: '9:10 am', waiting: 46 });
  else resumeAgent('recall');
}

/** `9 RECALLS · 6 RUNNING · WORKING`, or `… · ON HOLD` (§1, §6). */
export function recallEyebrow(): string {
  const rows = recallStore.rows;
  const running = rows.filter((r) => r.state === 'active').length;
  const state = recallHoldStore.onHold ? 'on hold' : 'working';
  return `${String(rows.length)} recalls · ${String(running)} running · ${state}`;
}

/** The Triage banner (§1, §7) — the queue counts itself. */
export function recallWaiting(): { count: number; names: string } {
  const q = recallCallQueue();
  return { count: q.total, names: q.sentence };
}
